// components/models/QwenImage2ProControlPanel.tsx
import React, { memo, useCallback } from 'react';
import AutoGrowingTextarea from '../AutoGrowingTextarea';
import { QwenImage2ProParams } from '#types/types';
import { useKernel } from '@/kernel/Kernel';

interface QwenImage2ProControlPanelProps {
  params: QwenImage2ProParams;
  setModelParams: (
    updater: (
      prevParams: { [model: string]: QwenImage2ProParams }
    ) => { [model: string]: QwenImage2ProParams }
  ) => void;
  diffusionModel: string;
}

const EMPTY_TEXTAREA_STYLE_OVERRIDES = {};

export const getDefaultQwenImage2ProParams = (): QwenImage2ProParams => ({
  prompt: '',
  seed: -1,
  negative_prompt: '',
});

const QwenImage2ProControlPanel: React.FC<QwenImage2ProControlPanelProps> = ({
  params,
  setModelParams,
  diffusionModel,
}) => {
  const { kernel } = useKernel();

  const updateParam = useCallback((
    name: keyof QwenImage2ProParams,
    value: QwenImage2ProParams[keyof QwenImage2ProParams],
  ) => {
    setModelParams((prevModelParams) => {
      const currentParams = prevModelParams[diffusionModel];
      if (!currentParams) {
        console.error(`[QwenImage2ProControlPanel] Missing params for model "${diffusionModel}".`);
        return prevModelParams;
      }
      return {
        ...prevModelParams,
        [diffusionModel]: {
          ...currentParams,
          [name]: value,
        },
      };
    });

    kernel.current?.logUserAction('Setting Changed', {
      setting_name: name,
      value: name === 'prompt' || name === 'negative_prompt' ? undefined : value,
      value_length: name === 'prompt' || name === 'negative_prompt' ? String(value || '').length : undefined,
      model_name: diffusionModel,
      source_component: 'QwenImage2ProControlPanel',
    });
  }, [diffusionModel, setModelParams, kernel]);

  const handleTextParamChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    updateParam(name as keyof QwenImage2ProParams, value as QwenImage2ProParams[keyof QwenImage2ProParams]);
  }, [updateParam]);

  return (
    <>
      <div className="mb-4">
        <AutoGrowingTextarea
          label="Prompt"
          placeholder="Describe how to edit or generate the image..."
          description="Main instruction used for generation or image editing."
          name="prompt"
          value={params.prompt}
          onChange={handleTextParamChange}
          onEnterPress={kernel.current?.triggerGenerate}
          maxHeight={600}
          labelClassName="block text-lg font-semibold text-white mb-2"
        />
      </div>

      <div className="mb-4">
        <AutoGrowingTextarea
          label="Negative Prompt"
          placeholder="Describe what to avoid..."
          description="Optional elements/styles to exclude."
          name="negative_prompt"
          value={params.negative_prompt}
          onChange={handleTextParamChange}
          onEnterPress={kernel.current?.triggerGenerate}
          maxHeight={240}
          labelClassName="block text-sm font-medium text-white mb-1"
        />
      </div>

    </>
  );
};

export default memo(QwenImage2ProControlPanel);

